import type { AssetId, Concept, ConceptId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { TbMessage2, TbX } from "solid-icons/tb";
import { For, Show, Suspense, createMemo, createSignal } from "solid-js";
import { createStore } from "solid-js/store";

import { clientOnly } from "@solidjs/start";
import { dispatchGlobalEvent } from "@storytell/events";
import { NavigationConceptsPaneDetails } from "~/components/concepts/NavigationConceptsPaneDetails";
import { useMessageHasKnowledgeSearchAssets } from "~/utils/messageHasKnowledgeSearch";
import { useWire } from "~/wire";
import { useAppUI } from "./AppUIContext";
import { ExtendedPaneMode } from "./AppUIContext";

const ConceptsGraph = clientOnly(() =>
  import("~/components/concepts/ConceptsGraph").then((m) => ({ default: m.ConceptsGraph })),
);
export const ExtendedPaneConceptsPowering = () => {
  const wire = useWire();
  const { state, actions } = useAppUI();
  const hasConceptsCheck = useMessageHasKnowledgeSearchAssets();
  const activeMessage = () => wire.chat.getChatMessages()?.findLast((m) => hasConceptsCheck(m));
  const activeConcepts = createMemo(() => {
    const m = activeMessage();
    if (!m) return [];
    const concepts = new Set<ConceptId>();
    for (const o of m.operations) {
      const op = wire.stream.operation.get(o);
      if (op?.activity === "user_kb_search_v1") {
        for (const blockId of op.blocks) {
          const block = wire.stream.block.get(blockId);
          if (!block) continue;

          if (block.searchResults) {
            if (block.searchResults.results.case === "assets") {
              for (const asset of block.searchResults.results.value.assets) {
                for (const conceptId of wire.sync.getAssetRelatedConcepts(asset.id as AssetId)) concepts.add(conceptId);
              }
            }
          }
        }
      }
    }
    return Array.from(concepts).map((c) => wire.sync.concepts.get(c)) as Concept[];
  });
  const [sliderScore, setSliderScore] = createStore({
    min: 0,
    max: 0,
    current: 0,
  });
  const [graphSelectedConcept, setGraphSelectedConcept] = createSignal<ConceptId | "">("");

  const onClose = () => {
    actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN);
    actions.setExtendedPaneConceptsPowering(false);
    wire.ui.conceptsPoweringChat.setClosed(true);
  };

  return (
    <div class="h-full max-h-full py-4 px-2 bg-panel border-l border-primary-border">
      <div class="flex items-center justify-between mb-5 px-2">
        <h3 class="font-medium text-primary-text text-lg">
          Concepts Powering this Chat
          <a
            class="text-xs text-brand ml-3 font-normal"
            href="https://go.storytell.ai/learn-concepts"
            target="_blank"
            rel="noreferrer"
          >
            Learn More
          </a>
        </h3>
        <StButton icon={TbX} class="p-1 dark:border-primary-border" onClick={onClose}>
          Close
        </StButton>
      </div>
      <div class="h-1/3">
        <ConceptsGraph
          concepts={activeConcepts()}
          search=""
          buildFilterFunction={() => true}
          onConceptClick={() => {}}
          onGraphConceptClick={setGraphSelectedConcept}
          graphSelectedConcept={graphSelectedConcept()}
          score={{ ...sliderScore }}
          setScore={setSliderScore}
        />
      </div>

      <div class="overflow-y-auto max-w-full h-2/3 pb-20 pt-3">
        <Show
          when={graphSelectedConcept() !== ""}
          fallback={
            <div class="space-y-3">
              <For each={activeConcepts().slice(0, sliderScore.current + 1)}>
                {(c) => {
                  const [readMore, setReadMore] = createSignal(false);
                  return (
                    <div class="p-2 rounded-lg border border-primary-border">
                      <h4 class="font-medium text-sm mb-2">{c.name}</h4>

                      <p class="text-primary-text text-xs mb-1 leading-relaxed">
                        {c.summary}{" "}
                        <button
                          type="button"
                          class="text-brand underline cursor-pointer"
                          classList={{ hidden: readMore() }}
                          onClick={() => setReadMore(!readMore())}
                        >
                          Read More
                        </button>
                      </p>
                      <Show when={readMore()}>
                        <p class="text-secondary-text text-xs leading-relaxed">
                          {c.description}{" "}
                          <button
                            type="button"
                            class="text-brand underline cursor-pointer"
                            onClick={() => setReadMore(!readMore())}
                          >
                            Read Less
                          </button>
                        </p>
                      </Show>

                      <div class="flex items-center gap-2">
                        <StButton
                          icon={TbMessage2}
                          onClick={() => {
                            dispatchGlobalEvent("add-items-to-prompt-scope", { ids: [c.id] });
                          }}
                        >
                          Chat with this Concept
                        </StButton>

                        <StButton
                          noBorder
                          onClick={() => {
                            setGraphSelectedConcept(c.id);
                          }}
                        >
                          View details
                        </StButton>
                      </div>
                    </div>
                  );
                }}
              </For>
            </div>
          }
        >
          <Suspense>
            <NavigationConceptsPaneDetails
              variant="chat"
              id={graphSelectedConcept()}
              filterBuilder={undefined}
              onConceptClick={(c) => {
                setGraphSelectedConcept(c);
              }}
            />
          </Suspense>
        </Show>
      </div>
    </div>
  );
};
